import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  PiggyBank, Plus, ChevronLeft, ChevronRight,
  TrendingUp, TrendingDown, DollarSign, Target, Wallet,
  Edit2, Trash2, Receipt, ArrowUpCircle, ArrowDownCircle,
  FileText, CalendarPlus,
} from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, Legend, ResponsiveContainer,
  PieChart, Pie, Cell, AreaChart, Area, CartesianGrid,
} from 'recharts';
import { budgetAPI, fileViewUrl } from '../services/api';
import { toast } from '../components/common/Toast';
import Modal from '../components/common/Modal';
import { fmt, errMsg } from '../utils/helpers';

// ── Category constants (mirrors backend) ─────
const BUDGET_CATEGORIES = [
  { key: 'housing_rent',          group: 'housing',    type: 'expense' },
  { key: 'housing_mortgage',      group: 'housing',    type: 'expense' },
  { key: 'housing_insurance',     group: 'housing',    type: 'expense' },
  { key: 'housing_taxes',         group: 'housing',    type: 'expense' },
  { key: 'housing_hoa',           group: 'housing',    type: 'expense' },
  { key: 'housing_repairs',       group: 'housing',    type: 'expense' },
  { key: 'utilities_electricity', group: 'utilities',  type: 'expense' },
  { key: 'utilities_water',       group: 'utilities',  type: 'expense' },
  { key: 'utilities_gas',         group: 'utilities',  type: 'expense' },
  { key: 'utilities_internet',    group: 'utilities',  type: 'expense' },
  { key: 'utilities_phone',       group: 'utilities',  type: 'expense' },
  { key: 'utilities_trash',       group: 'utilities',  type: 'expense' },
  { key: 'food_groceries',        group: 'food',       type: 'expense' },
  { key: 'food_dining',           group: 'food',       type: 'expense' },
  { key: 'food_delivery',         group: 'food',       type: 'expense' },
  { key: 'transport_fuel',        group: 'transport',  type: 'expense' },
  { key: 'transport_insurance',   group: 'transport',  type: 'expense' },
  { key: 'transport_maintenance', group: 'transport',  type: 'expense' },
  { key: 'transport_public',      group: 'transport',  type: 'expense' },
  { key: 'transport_parking',     group: 'transport',  type: 'expense' },
  { key: 'health_insurance',      group: 'health',     type: 'expense' },
  { key: 'health_medications',    group: 'health',     type: 'expense' },
  { key: 'health_dental',         group: 'health',     type: 'expense' },
  { key: 'health_fitness',        group: 'health',     type: 'expense' },
  { key: 'leisure_entertainment', group: 'leisure',    type: 'expense' },
  { key: 'leisure_vacations',     group: 'leisure',    type: 'expense' },
  { key: 'leisure_celebrations',  group: 'leisure',    type: 'expense' },
  { key: 'leisure_subscriptions', group: 'leisure',    type: 'expense' },
  { key: 'savings_emergency',     group: 'savings',    type: 'expense' },
  { key: 'savings_investments',   group: 'savings',    type: 'expense' },
  { key: 'savings_retirement',    group: 'savings',    type: 'expense' },
  { key: 'education_tuition',     group: 'education',  type: 'expense' },
  { key: 'education_books',       group: 'education',  type: 'expense' },
  { key: 'education_childcare',   group: 'education',  type: 'expense' },
  { key: 'personal_clothing',     group: 'personal',   type: 'expense' },
  { key: 'personal_beauty',       group: 'personal',   type: 'expense' },
  { key: 'personal_gifts',        group: 'personal',   type: 'expense' },
  { key: 'income_salary',         group: 'income',     type: 'income'  },
  { key: 'income_freelance',      group: 'income',     type: 'income'  },
  { key: 'income_rental',         group: 'income',     type: 'income'  },
  { key: 'income_other',          group: 'income',     type: 'income'  },
  { key: 'other_misc',            group: 'other',      type: 'expense' },
];

const GROUP_COLORS = {
  housing:   '#6366f1', utilities: '#0ea5e9', food:      '#f59e0b',
  transport: '#10b981', health:    '#ec4899', leisure:   '#8b5cf6',
  savings:   '#14b8a6', education: '#f97316', personal:  '#e879f9',
  income:    '#22c55e', other:     '#94a3b8',
};

const EXPENSE_GROUPS = ['housing','utilities','food','transport','health','leisure','savings','education','personal','other'];
const EXPENSE_CATS   = BUDGET_CATEGORIES.filter(c => c.type === 'expense');
const INCOME_CATS    = BUDGET_CATEGORIES.filter(c => c.type === 'income');

function emptyCatMap() {
  const m = {};
  EXPENSE_CATS.forEach(c => { m[c.key] = ''; });
  return m;
}

function emptyTxForm() {
  return { type: 'expense', category: '', amount: '', description: '', date: new Date().toISOString().slice(0, 10), vendor: '', notes: '' };
}

function KpiCard({ label, value, sub, icon: Icon, color, trend }) {
  return (
    <div className="kpi-card" style={{ borderTop: `3px solid ${color}` }}>
      <div className="kpi-card-header">
        <span className="kpi-label">{label}</span>
        <div style={{ width: 32, height: 32, borderRadius: 8, background: color + '18', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Icon size={16} color={color} />
        </div>
      </div>
      <div className="kpi-value">{value}</div>
      {sub && <div className="kpi-sub">{sub}</div>}
    </div>
  );
}

const TOOLTIP_STYLE = {
  background: 'var(--surface)', border: '1px solid var(--border)',
  borderRadius: 8, fontSize: 12, color: 'var(--ink)',
};

export default function Budget() {
  const { t, i18n } = useTranslation();

  const now = new Date();
  const [period, setPeriod] = useState({ month: now.getMonth() + 1, year: now.getFullYear() });

  const [summary,      setSummary]      = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [loading,      setLoading]      = useState(true);

  // Plan modal
  const [planOpen,   setPlanOpen]   = useState(false);
  const [planForm,   setPlanForm]   = useState({ name: '', plannedIncome: '', allocations: emptyCatMap() });
  const [planSaving, setPlanSaving] = useState(false);

  // Transaction modal
  const [txOpen,    setTxOpen]    = useState(false);
  const [txEditing, setTxEditing] = useState(null);
  const [txForm,    setTxForm]    = useState(emptyTxForm());
  const [txFile,    setTxFile]    = useState(null);
  const [txSaving,  setTxSaving]  = useState(false);

  // Table filters
  const [txFilter, setTxFilter] = useState({ type: '', category: '', search: '' });

  // Copy + forecast
  const [copyOpen,  setCopyOpen]  = useState(false);
  const [copyForm,  setCopyForm]  = useState({ month: '', year: '' });
  const [copying,   setCopying]   = useState(false);
  const [forecast,  setForecast]  = useState([]);

  // ── Period helpers ─────────────────────────
  const locale       = i18n.language === 'es' ? 'es-MX' : 'en-US';
  const periodLabel  = new Date(period.year, period.month - 1, 1).toLocaleDateString(locale, { month: 'long', year: 'numeric' });
  const isThisMonth  = period.month === now.getMonth() + 1 && period.year === now.getFullYear();

  const prevMonth = () => setPeriod(p => p.month === 1  ? { month: 12, year: p.year - 1 } : { ...p, month: p.month - 1 });
  const nextMonth = () => {
    if (isThisMonth) return;
    setPeriod(p => p.month === 12 ? { month: 1, year: p.year + 1 } : { ...p, month: p.month + 1 });
  };

  // ── Data fetching ──────────────────────────
  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [sumRes, txRes] = await Promise.all([
        budgetAPI.getSummary({ month: period.month, year: period.year }),
        budgetAPI.getTransactions({ month: period.month, year: period.year }),
      ]);
      setSummary(sumRes.data.data);
      setTransactions(txRes.data.data);
    } catch {
      toast.error(t('failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [period.month, period.year, t]);

  const fetchForecast = useCallback(async () => {
    try {
      const res = await budgetAPI.getForecast({ fromMonth: period.month, fromYear: period.year, months: 6 });
      setForecast(res.data.data);
    } catch { /* silently ignore — forecast is non-critical */ }
  }, [period.month, period.year]);

  useEffect(() => { fetchData(); fetchForecast(); }, [fetchData, fetchForecast]);

  // ── Plan modal ─────────────────────────────
  const openPlanModal = () => {
    const allocs = emptyCatMap();
    if (summary?.plan) {
      summary.plan.allocations.forEach(a => { allocs[a.category] = String(a.planned); });
    }
    setPlanForm({
      name:          summary?.plan?.name || t('defaultPlanName'),
      plannedIncome: summary?.plan?.plannedIncome ? String(summary.plan.plannedIncome) : '',
      allocations:   allocs,
    });
    setPlanOpen(true);
  };

  const savePlan = async () => {
    if (!planForm.name.trim()) return toast.error(t('required'));
    setPlanSaving(true);
    try {
      const payload = {
        year:          period.year,
        month:         period.month,
        name:          planForm.name,
        plannedIncome: parseFloat(planForm.plannedIncome) || 0,
        allocations:   Object.entries(planForm.allocations)
          .filter(([, v]) => v && parseFloat(v) > 0)
          .map(([category, planned]) => ({ category, planned: parseFloat(planned) })),
      };
      if (summary?.plan?._id) {
        await budgetAPI.updatePlan(summary.plan._id, payload);
      } else {
        await budgetAPI.createPlan(payload);
      }
      toast.success(t('budgetPlanSaved'));
      setPlanOpen(false);
      fetchData();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setPlanSaving(false);
    }
  };

  const deletePlan = () => {
    if (!summary?.plan?._id) return;
    toast.confirm(t('deletePlanConfirm'), async () => {
      await budgetAPI.deletePlan(summary.plan._id);
      toast.success(t('budgetPlanDeleted'));
      fetchData();
    });
  };

  // ── Copy modal ─────────────────────────────
  const openCopyModal = () => {
    const nm = period.month === 12 ? 1  : period.month + 1;
    const ny = period.month === 12 ? period.year + 1 : period.year;
    setCopyForm({ month: String(nm), year: String(ny) });
    setCopyOpen(true);
  };

  const doCopy = async () => {
    if (!summary?.plan?._id || !copyForm.month || !copyForm.year) return;
    setCopying(true);
    try {
      await budgetAPI.copyPlan(summary.plan._id, { targetMonth: Number(copyForm.month), targetYear: Number(copyForm.year) });
      toast.success(t('budgetCopied'));
      setCopyOpen(false);
      fetchForecast();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setCopying(false);
    }
  };

  // ── Transaction modal ──────────────────────
  const openTxModal = (tx = null) => {
    setTxEditing(tx);
    setTxFile(null);
    setTxForm(tx ? {
      type:        tx.type,
      category:    tx.category,
      amount:      String(tx.amount),
      description: tx.description,
      date:        tx.date.slice(0, 10),
      vendor:      tx.vendor || '',
      notes:       tx.notes  || '',
    } : emptyTxForm());
    setTxOpen(true);
  };

  const saveTx = async () => {
    if (!txForm.category || !txForm.type || !txForm.amount || !txForm.description) {
      return toast.error(t('required'));
    }
    setTxSaving(true);
    try {
      const fd = new FormData();
      Object.entries(txForm).forEach(([k, v]) => { if (v) fd.append(k, v); });
      if (summary?.plan?._id) fd.append('planId', summary.plan._id);
      if (txFile) fd.append('receipt', txFile);

      if (txEditing) {
        await budgetAPI.updateTransaction(txEditing._id, fd);
      } else {
        await budgetAPI.createTransaction(fd);
      }
      toast.success(t('transactionSaved'));
      setTxOpen(false);
      fetchData();
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setTxSaving(false);
    }
  };

  const deleteTx = tx => {
    toast.confirm(`${t('deleteTxConfirm')}`, async () => {
      await budgetAPI.deleteTransaction(tx._id);
      toast.success(t('transactionDeleted'));
      fetchData();
    });
  };

  // ── Chart data ─────────────────────────────
  const barData = useMemo(() => {
    if (!summary) return [];
    return EXPENSE_GROUPS.map(group => {
      const cats    = BUDGET_CATEGORIES.filter(c => c.group === group && c.type === 'expense');
      const planned = cats.reduce((s, c) => s + (summary.plannedMap[c.key] || 0), 0);
      const actual  = cats.reduce((s, c) => s + (summary.actualMap[c.key]?.expense || 0), 0);
      return { name: t(`bgrp_${group}`), planned, actual, color: GROUP_COLORS[group] };
    }).filter(g => g.planned > 0 || g.actual > 0);
  }, [summary, t]);

  const pieData = useMemo(() => {
    if (!summary) return [];
    return EXPENSE_GROUPS.map(group => {
      const cats  = BUDGET_CATEGORIES.filter(c => c.group === group && c.type === 'expense');
      const value = cats.reduce((s, c) => s + (summary.actualMap[c.key]?.expense || 0), 0);
      return { name: t(`bgrp_${group}`), value, color: GROUP_COLORS[group] };
    }).filter(g => g.value > 0);
  }, [summary, t]);

  const trendData = useMemo(() => {
    if (!summary?.trend) return [];
    return summary.trend.map(item => ({
      name:   new Date(item._id.year, item._id.month - 1, 1).toLocaleDateString(locale, { month: 'short' }),
      amount: item.total,
    }));
  }, [summary, locale]);

  // ── Projected pie (plan allocation breakdown, shown when no transactions) ──
  const projectedPieData = useMemo(() => {
    if (!summary?.plan || transactions.length > 0) return [];
    const groupTotals = {};
    summary.plan.allocations.forEach(a => {
      const cat = BUDGET_CATEGORIES.find(c => c.key === a.category);
      if (!cat) return;
      groupTotals[cat.group] = (groupTotals[cat.group] || 0) + a.planned;
    });
    return Object.entries(groupTotals)
      .map(([group, value]) => ({ name: t(`bgrp_${group}`), value, color: GROUP_COLORS[group] }))
      .filter(g => g.value > 0)
      .sort((a, b) => b.value - a.value);
  }, [summary, transactions.length, t]);

  // ── Filtered transactions ──────────────────
  const filteredTxs = useMemo(() => {
    return transactions.filter(tx => {
      if (txFilter.type     && tx.type     !== txFilter.type)     return false;
      if (txFilter.category && tx.category !== txFilter.category) return false;
      if (txFilter.search) {
        const q = txFilter.search.toLowerCase();
        if (!tx.description?.toLowerCase().includes(q) && !tx.vendor?.toLowerCase().includes(q)) return false;
      }
      return true;
    });
  }, [transactions, txFilter]);

  // ── Category options for the tx modal ─────
  const txCatOptions = txForm.type === 'income' ? INCOME_CATS : EXPENSE_CATS;

  // ── Categories grouped for plan modal ─────
  const catsByGroup = useMemo(() => {
    const groups = {};
    EXPENSE_CATS.forEach(c => {
      if (!groups[c.group]) groups[c.group] = [];
      groups[c.group].push(c);
    });
    return groups;
  }, []);

  // ── Render ─────────────────────────────────
  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 300 }}>
        <div className="spinner" />
      </div>
    );
  }

  const s = summary || { plannedIncome: 0, totalPlanned: 0, totalActualExp: 0, netBalance: 0, savingsRate: 0, actualMap: {}, plannedMap: {}, trend: [], plan: null };
  const isOver     = s.totalActualExp > s.totalPlanned && s.totalPlanned > 0;
  const balancePos = s.netBalance >= 0;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 700, display: 'flex', alignItems: 'center', gap: 8 }}>
            <PiggyBank size={22} color="var(--brand)" /> {t('budgetModule')}
          </h1>
          <p style={{ fontSize: 13, color: 'var(--ink-40)', marginTop: 2 }}>{t('budgetSubtitle')}</p>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Period nav */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8, padding: '4px 8px' }}>
            <button className="btn btn-ghost btn-icon" onClick={prevMonth} style={{ padding: 4 }}>
              <ChevronLeft size={16} />
            </button>
            <span style={{ fontSize: 14, fontWeight: 600, minWidth: 140, textAlign: 'center', textTransform: 'capitalize' }}>{periodLabel}</span>
            <button className="btn btn-ghost btn-icon" onClick={nextMonth} disabled={isThisMonth} style={{ padding: 4, opacity: isThisMonth ? 0.3 : 1 }}>
              <ChevronRight size={16} />
            </button>
          </div>

          {s.plan && (
            <button className="btn btn-ghost" onClick={openCopyModal} title={t('copyBudget')}>
              <CalendarPlus size={15} /> {t('copyBudget')}
            </button>
          )}
          <button className="btn btn-secondary" onClick={openPlanModal}>
            <Target size={15} /> {s.plan ? t('editBudgetPlan') : t('newBudgetPlan')}
          </button>
          <button className="btn btn-primary" onClick={() => openTxModal()}>
            <Plus size={15} /> {t('newTransaction')}
          </button>
        </div>
      </div>

      {/* ── No plan banner ── */}
      {!s.plan && (
        <div style={{ background: 'var(--brand)08', border: '1px dashed var(--brand)40', borderRadius: 10, padding: '14px 18px', display: 'flex', alignItems: 'center', gap: 12 }}>
          <Target size={20} color="var(--brand)" style={{ flexShrink: 0 }} />
          <div>
            <div style={{ fontWeight: 600, fontSize: 14 }}>{t('noBudgetPlan')}</div>
            <div style={{ fontSize: 12, color: 'var(--ink-40)', marginTop: 2 }}>{t('createFirstPlan')}</div>
          </div>
          <button className="btn btn-primary" style={{ marginLeft: 'auto' }} onClick={openPlanModal}>
            {t('newBudgetPlan')}
          </button>
        </div>
      )}

      {/* ── KPI Cards ── */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 16 }}>
        <KpiCard label={t('plannedIncome')}   value={fmt.currency(s.plannedIncome)}  icon={ArrowUpCircle}  color="#22c55e" />
        <KpiCard label={t('totalPlanned')}    value={fmt.currency(s.totalPlanned)}   icon={Target}         color="#6366f1" />
        <KpiCard label={t('actualSpending')}  value={fmt.currency(s.totalActualExp)} icon={ArrowDownCircle} color={isOver ? '#ef4444' : '#f59e0b'}
          sub={isOver ? t('overBudget') : s.totalPlanned > 0 ? t('onTrack') : undefined} />
        <KpiCard label={t('netBalance')}      value={fmt.currency(s.netBalance)}     icon={Wallet}         color={balancePos ? '#14b8a6' : '#ef4444'} />
        <KpiCard label={t('savingsRate')}     value={`${s.savingsRate}%`}            icon={TrendingUp}     color="#8b5cf6" />
      </div>

      {/* ── Charts Row ── */}
      {(barData.length > 0 || pieData.length > 0) && (
        <div style={{ display: 'grid', gridTemplateColumns: pieData.length ? '1fr 340px' : '1fr', gap: 16 }}>

          {/* Budget vs Actual bar chart */}
          {barData.length > 0 && (
            <div className="card" style={{ padding: '20px 16px 12px' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>{t('budgetVsActual')}</div>
              <ResponsiveContainer width="100%" height={240}>
                <BarChart data={barData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                  <YAxis tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} width={52} />
                  <Tooltip formatter={v => fmt.currency(v)} contentStyle={TOOLTIP_STYLE} />
                  <Legend wrapperStyle={{ fontSize: 12 }} />
                  <Bar dataKey="planned" name={t('planned')} fill="#6366f1" radius={[4,4,0,0]} />
                  <Bar dataKey="actual"  name={t('actual')}  fill="#f59e0b" radius={[4,4,0,0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}

          {/* Expense Breakdown pie */}
          {pieData.length > 0 && (
            <div className="card" style={{ padding: '20px 16px 12px' }}>
              <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 8 }}>{t('expenseBreakdown')}</div>
              <ResponsiveContainer width="100%" height={160}>
                <PieChart>
                  <Pie data={pieData} cx="50%" cy="50%" innerRadius={48} outerRadius={72} dataKey="value" paddingAngle={2}>
                    {pieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={v => fmt.currency(v)} contentStyle={TOOLTIP_STYLE} />
                </PieChart>
              </ResponsiveContainer>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 4, marginTop: 6 }}>
                {pieData.map(item => (
                  <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11 }}>
                    <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                    <span style={{ flex: 1, color: 'var(--ink-60)' }}>{item.name}</span>
                    <span style={{ fontWeight: 600 }}>{fmt.currency(item.value)}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Monthly Trend ── */}
      {trendData.length > 1 && (
        <div className="card" style={{ padding: '20px 16px 12px' }}>
          <div style={{ fontWeight: 600, fontSize: 14, marginBottom: 16 }}>{t('monthlyTrend')}</div>
          <ResponsiveContainer width="100%" height={180}>
            <AreaChart data={trendData} margin={{ top: 4, right: 8, left: 0, bottom: 0 }}>
              <defs>
                <linearGradient id="budgetGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%"  stopColor="#6366f1" stopOpacity={0.25} />
                  <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--border)" />
              <XAxis dataKey="name" tick={{ fontSize: 11 }} />
              <YAxis tickFormatter={v => `$${v >= 1000 ? `${(v/1000).toFixed(0)}k` : v}`} tick={{ fontSize: 11 }} width={52} />
              <Tooltip formatter={v => fmt.currency(v)} contentStyle={TOOLTIP_STYLE} />
              <Area type="monotone" dataKey="amount" name={t('actualSpending')} stroke="#6366f1" fill="url(#budgetGrad)" strokeWidth={2} dot={{ r: 3 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      )}

      {/* ── Forecast report when plan exists but no transactions ── */}
      {projectedPieData.length > 0 && (
        <div className="card" style={{ padding: '20px 18px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
            <TrendingUp size={16} color="var(--brand)" />
            <span style={{ fontWeight: 600, fontSize: 14 }}>{t('projectedSpending')}</span>
            <span style={{ fontSize: 11, padding: '2px 10px', background: 'var(--brand)15', color: 'var(--brand)', borderRadius: 20, fontWeight: 600, marginLeft: 4 }}>{t('forecastMode')}</span>
          </div>
          <p style={{ fontSize: 12, color: 'var(--ink-40)', marginBottom: 20 }}>{t('forecastNote')}</p>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 20, alignItems: 'center' }}>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={projectedPieData} cx="50%" cy="50%" innerRadius={58} outerRadius={90} dataKey="value" paddingAngle={2}>
                  {projectedPieData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={v => fmt.currency(v)} contentStyle={TOOLTIP_STYLE} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {projectedPieData.map(item => (
                <div key={item.name} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <div style={{ width: 10, height: 10, borderRadius: 3, background: item.color, flexShrink: 0 }} />
                  <span style={{ flex: 1, fontSize: 12, color: 'var(--ink-60)' }}>{item.name}</span>
                  <span style={{ fontWeight: 600, fontSize: 12 }}>{fmt.currency(item.value)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* ── Transactions ── */}
      <div className="card">
        <div style={{ padding: '16px 18px 0', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 10, paddingBottom: 12 }}>
          <div style={{ fontWeight: 600, fontSize: 14 }}>{t('recentActivity')}</div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <input
              className="form-input"
              style={{ width: 180 }}
              placeholder={t('searchTransactions')}
              value={txFilter.search}
              onChange={e => setTxFilter(f => ({ ...f, search: e.target.value }))}
            />
            <select className="form-input" style={{ width: 130 }} value={txFilter.type}
              onChange={e => setTxFilter(f => ({ ...f, type: e.target.value, category: '' }))}>
              <option value="">{t('allTypes')}</option>
              <option value="income">{t('income')}</option>
              <option value="expense">{t('expense')}</option>
            </select>
            <select className="form-input" style={{ width: 160 }} value={txFilter.category}
              onChange={e => setTxFilter(f => ({ ...f, category: e.target.value }))}>
              <option value="">{t('allCategories')}</option>
              {(txFilter.type === 'income' ? INCOME_CATS : txFilter.type === 'expense' ? EXPENSE_CATS : BUDGET_CATEGORIES).map(c => (
                <option key={c.key} value={c.key}>{t(`bcat_${c.key}`)}</option>
              ))}
            </select>
          </div>
        </div>

        {filteredTxs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px 0', color: 'var(--ink-40)' }}>
            <FileText size={32} strokeWidth={1.2} style={{ marginBottom: 8, opacity: 0.4 }} />
            <div>{t('noTransactions')}</div>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="table">
              <thead>
                <tr>
                  <th>{t('date')}</th>
                  <th>{t('description')}</th>
                  <th>{t('category')}</th>
                  <th>{t('transactionType')}</th>
                  <th>{t('amount')}</th>
                  <th>{t('vendor')}</th>
                  <th style={{ textAlign: 'center' }}>{t('receipt')}</th>
                  <th style={{ textAlign: 'right' }}>{t('actions')}</th>
                </tr>
              </thead>
              <tbody>
                {filteredTxs.map(tx => (
                  <tr key={tx._id}>
                    <td style={{ whiteSpace: 'nowrap', fontSize: 12 }}>{fmt.shortDate(tx.date)}</td>
                    <td style={{ maxWidth: 200 }}>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{tx.description}</div>
                    </td>
                    <td>
                      <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 20, background: (GROUP_COLORS[tx.category?.split('_')[0]] || '#94a3b8') + '20', color: GROUP_COLORS[tx.category?.split('_')[0]] || '#64748b', fontWeight: 600 }}>
                        {t(`bcat_${tx.category}`)}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${tx.type === 'income' ? 'badge-active' : 'badge-pending'}`}>
                        {t(tx.type)}
                      </span>
                    </td>
                    <td style={{ fontWeight: 700, color: tx.type === 'income' ? 'var(--emerald)' : 'var(--ink)', fontSize: 14 }}>
                      {tx.type === 'income' ? '+' : '-'}{fmt.currency(tx.amount)}
                    </td>
                    <td style={{ fontSize: 12, color: 'var(--ink-60)' }}>{tx.vendor || '—'}</td>
                    <td style={{ textAlign: 'center' }}>
                      {tx.receiptKey ? (
                        <a href={fileViewUrl(tx.receiptKey)} target="_blank" rel="noopener noreferrer" className="btn btn-ghost btn-icon" title={t('viewReceipt')}>
                          <Receipt size={15} color="var(--brand)" />
                        </a>
                      ) : '—'}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: 4, justifyContent: 'flex-end' }}>
                        <button className="btn btn-ghost btn-icon" onClick={() => openTxModal(tx)} title={t('edit')}>
                          <Edit2 size={14} />
                        </button>
                        <button className="btn btn-ghost btn-icon" onClick={() => deleteTx(tx)} title={t('delete')} style={{ color: 'var(--rose)' }}>
                          <Trash2 size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── 6-Month Outlook ── */}
      {forecast.length > 0 && (
        <div className="card">
          <div style={{ padding: '14px 18px', borderBottom: '1px solid var(--border)', display: 'flex', alignItems: 'center', gap: 8 }}>
            <TrendingUp size={16} color="var(--brand)" />
            <span style={{ fontWeight: 600, fontSize: 14 }}>{t('sixMonthOutlook')}</span>
            <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--ink-30)' }}>{t('clickToNavigate')}</span>
          </div>
          <div style={{ overflowX: 'auto' }}>
            <div style={{ display: 'grid', gridTemplateColumns: `repeat(${forecast.length}, minmax(130px, 1fr))`, minWidth: forecast.length * 130 }}>
              {forecast.map((item, i) => {
                const label      = new Date(item.year, item.month - 1, 1).toLocaleDateString(locale, { month: 'short', year: '2-digit' });
                const pct        = item.totalPlanned > 0 ? Math.min(100, Math.round((item.actualTotal / item.totalPlanned) * 100)) : 0;
                const isSelected = item.month === period.month && item.year === period.year;
                const over       = item.actualTotal > item.totalPlanned && item.totalPlanned > 0;
                return (
                  <div key={i} onClick={() => setPeriod({ month: item.month, year: item.year })}
                    style={{ padding: '14px 12px', cursor: 'pointer', borderRight: i < forecast.length - 1 ? '1px solid var(--border)' : 'none', background: isSelected ? 'var(--brand)08' : 'transparent', transition: 'background 0.15s' }}>
                    <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.05em', color: isSelected ? 'var(--brand)' : 'var(--ink-40)', marginBottom: 8 }}>{label}</div>
                    {item.plan ? (
                      <>
                        <div style={{ fontSize: 15, fontWeight: 700 }}>{fmt.currency(item.totalPlanned)}</div>
                        <div style={{ fontSize: 10, color: 'var(--ink-40)', marginTop: 1 }}>{t('planned')}</div>
                        {item.hasTransactions ? (
                          <>
                            <div style={{ fontSize: 13, fontWeight: 600, marginTop: 8, color: over ? 'var(--rose)' : 'var(--emerald)' }}>{fmt.currency(item.actualTotal)}</div>
                            <div style={{ marginTop: 4, height: 4, background: 'var(--border)', borderRadius: 4, overflow: 'hidden' }}>
                              <div style={{ height: '100%', width: `${pct}%`, background: over ? 'var(--rose)' : 'var(--brand)', borderRadius: 4, transition: 'width 0.5s ease' }} />
                            </div>
                            <div style={{ fontSize: 10, color: 'var(--ink-30)', marginTop: 2 }}>{pct}% {t('spent')}</div>
                          </>
                        ) : (
                          <div style={{ marginTop: 8, fontSize: 10, color: 'var(--brand)', fontWeight: 600, padding: '2px 6px', background: 'var(--brand)12', borderRadius: 4, display: 'inline-block' }}>{t('forecastMode')}</div>
                        )}
                      </>
                    ) : (
                      <div style={{ fontSize: 11, color: 'var(--ink-20)', marginTop: 4 }}>{t('noPlan')}</div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {/* ── Plan Modal ── */}
      <Modal
        open={planOpen}
        onClose={() => setPlanOpen(false)}
        title={s.plan ? t('editBudgetPlan') : t('newBudgetPlan')}
        size="lg"
        footer={
          <>
            {s.plan && (
              <button className="btn btn-danger" onClick={() => { setPlanOpen(false); deletePlan(); }} style={{ marginRight: 'auto' }}>
                <Trash2 size={14} /> {t('delete')}
              </button>
            )}
            <button className="btn btn-secondary" onClick={() => setPlanOpen(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={savePlan} disabled={planSaving}>
              {planSaving ? t('saving') : t('save')}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">{t('budgetPlanName')} *</label>
              <input className="form-input" value={planForm.name}
                onChange={e => setPlanForm(f => ({ ...f, name: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('plannedIncome')} ($)</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={planForm.plannedIncome}
                onChange={e => setPlanForm(f => ({ ...f, plannedIncome: e.target.value }))} />
            </div>
          </div>

          <div>
            <div style={{ fontSize: 13, fontWeight: 600, marginBottom: 14, color: 'var(--ink)' }}>{t('allocations')}</div>
            {Object.entries(catsByGroup).map(([group, cats]) => (
              <div key={group} style={{ marginBottom: 18 }}>
                <div style={{ fontSize: 11, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '.07em', color: GROUP_COLORS[group], marginBottom: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <div style={{ width: 8, height: 8, borderRadius: '50%', background: GROUP_COLORS[group] }} />
                  {t(`bgrp_${group}`)}
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(170px, 1fr))', gap: 8 }}>
                  {cats.map(cat => (
                    <div key={cat.key}>
                      <label style={{ fontSize: 11, color: 'var(--ink-60)', marginBottom: 3, display: 'block' }}>{t(`bcat_${cat.key}`)}</label>
                      <input
                        className="form-input"
                        style={{ padding: '6px 10px', fontSize: 13 }}
                        type="number" min="0" step="0.01" placeholder="0"
                        value={planForm.allocations[cat.key] || ''}
                        onChange={e => setPlanForm(f => ({ ...f, allocations: { ...f.allocations, [cat.key]: e.target.value } }))}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal>

      {/* ── Transaction Modal ── */}
      <Modal
        open={txOpen}
        onClose={() => setTxOpen(false)}
        title={txEditing ? t('editTransaction') : t('newTransaction')}
        size="md"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setTxOpen(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={saveTx} disabled={txSaving}>
              {txSaving ? t('saving') : t('save')}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">{t('transactionType')} *</label>
              <select className="form-input" value={txForm.type}
                onChange={e => setTxForm(f => ({ ...f, type: e.target.value, category: '' }))}>
                <option value="expense">{t('expense')}</option>
                <option value="income">{t('income')}</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('category')} *</label>
              <select className="form-input" value={txForm.category}
                onChange={e => setTxForm(f => ({ ...f, category: e.target.value }))}>
                <option value="">{t('select')}</option>
                {txCatOptions.map(c => (
                  <option key={c.key} value={c.key}>{t(`bcat_${c.key}`)}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('description')} *</label>
            <input className="form-input" value={txForm.description}
              placeholder={txForm.type === 'income' ? t('bcat_income_other') : t('expenseDescriptionPlaceholder')}
              onChange={e => setTxForm(f => ({ ...f, description: e.target.value }))} />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div className="form-group">
              <label className="form-label">{t('amount')} ($) *</label>
              <input className="form-input" type="number" min="0" step="0.01"
                value={txForm.amount}
                onChange={e => setTxForm(f => ({ ...f, amount: e.target.value }))} />
            </div>
            <div className="form-group">
              <label className="form-label">{t('date')} *</label>
              <input className="form-input" type="date" value={txForm.date}
                onChange={e => setTxForm(f => ({ ...f, date: e.target.value }))} />
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">{t('vendor')}</label>
            <input className="form-input" value={txForm.vendor} placeholder={t('vendorPlaceholder')}
              onChange={e => setTxForm(f => ({ ...f, vendor: e.target.value }))} />
          </div>

          <div className="form-group">
            <label className="form-label">{t('notes')}</label>
            <textarea className="form-input" rows={2} value={txForm.notes} placeholder={t('optional')}
              onChange={e => setTxForm(f => ({ ...f, notes: e.target.value }))} />
          </div>

          <div className="form-group">
            <label className="form-label">{t('receiptAttachment')}</label>
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', padding: '8px 12px', border: '1px dashed var(--border)', borderRadius: 8, fontSize: 13, color: 'var(--ink-60)' }}>
              <Receipt size={16} />
              {txFile ? txFile.name : (txEditing?.receiptKey ? t('viewCurrent') : t('clickToUpload'))}
              <input type="file" accept=".pdf,.png,.jpg,.jpeg,.webp" style={{ display: 'none' }}
                onChange={e => setTxFile(e.target.files[0] || null)} />
            </label>
            {txEditing?.receiptKey && !txFile && (
              <a href={fileViewUrl(txEditing.receiptKey)} target="_blank" rel="noopener noreferrer"
                className="btn btn-ghost" style={{ marginTop: 4, fontSize: 12 }}>
                <Receipt size={13} /> {t('viewReceipt')}
              </a>
            )}
          </div>
        </div>
      </Modal>

      {/* ── Copy Modal ── */}
      <Modal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        title={t('copyBudgetTitle')}
        size="sm"
        footer={
          <>
            <button className="btn btn-secondary" onClick={() => setCopyOpen(false)}>{t('cancel')}</button>
            <button className="btn btn-primary" onClick={doCopy} disabled={copying}>
              {copying ? t('saving') : t('copyBudget')}
            </button>
          </>
        }
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ fontSize: 13, color: 'var(--ink-60)', padding: '10px 14px', background: 'var(--surface)', borderRadius: 8, border: '1px solid var(--border)' }}>
            {t('copyBudgetDesc')}: <strong style={{ textTransform: 'capitalize' }}>{periodLabel}</strong>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div className="form-group">
              <label className="form-label">{t('targetMonth')}</label>
              <select className="form-input" value={copyForm.month}
                onChange={e => setCopyForm(f => ({ ...f, month: e.target.value }))}>
                {[...Array(12)].map((_, i) => {
                  const mn = new Date(2024, i, 1).toLocaleDateString(locale, { month: 'long' });
                  return <option key={i + 1} value={i + 1} style={{ textTransform: 'capitalize' }}>{mn}</option>;
                })}
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">{t('targetYear')}</label>
              <select className="form-input" value={copyForm.year}
                onChange={e => setCopyForm(f => ({ ...f, year: e.target.value }))}>
                {[now.getFullYear(), now.getFullYear() + 1, now.getFullYear() + 2].map(y => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        </div>
      </Modal>
    </div>
  );
}
